/**
 * Every study plan the platform can see, in one list.
 *
 * **Why this exists:** most students are in no SchoolClass, so before
 * platform-owned plans there was nowhere for a good default CBSE-10 maths plan
 * to live — the only authorable plan belonged to a class. This is that home, and
 * deliberately not a fake SchoolClass, which would put a phantom class in every
 * roster.
 *
 * It renders the SAME table and editor a class's Plan tab renders — `PlansPanel`,
 * over the same shared chapter fetch — because a plan is one thing whoever owns
 * it. This page used to hold two: an authoring panel for OUR plans and a
 * hand-rolled table for published ones, behind a scope select. Two tables of the
 * same entity, with different columns, is how one surface becomes two products —
 * and the split made the reader decide which list a plan would be in before they
 * could look for it.
 *
 * One list now, narrowed by who published it, its reach, and the level it
 * targets. What CANNOT appear is a PRIVATE class or student plan: the repository
 * deliberately offers no way to enumerate those, and that is the feature.
 *
 * See docs/FeatureDesigns/goals-syllabus-revision-plan/StudyPlanning/shared-plans.md §4.2, §4.7.
 */
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SearchFilterBar, type FilterDef } from '@shared-ui/components';
import { useSubjectLabel } from '@shared-ui/reference-data';
import { ALL_SUBJECT_OPTIONS } from '@padcanvas/shared';
import { PlansPanel, type StudyPlan } from '@shared-ui/class-shared';
import { curriculumApi } from '../api/curriculumApi';
import { useChaptersFor } from './classTabs/useChaptersFor';
import { classSharedApi } from '../api/schoolsApi';
import { studyPlansApi } from '../api/studyPlansApi';
import './Schools.css';

/** Who published it — the three owners the table's From column names. */
const FROM_OPTIONS = [
  { value: 'platform', label: 'Us' },
  { value: 'schoolClass', label: 'A class' },
  { value: 'user', label: 'A student' },
];

/** Reach. Labels match the editor's visibility field — one vocabulary. */
const VISIBILITY_OPTIONS = [
  { value: 'listed', label: 'Anyone' },
  { value: 'class', label: 'Your class' },
  { value: 'private', label: 'Only you' },
];

export function StudyPlans() {
  const subjectLabel = useSubjectLabel();
  // Seeded from the URL so a link can land here already pointed at one
  // syllabus — arriving from a curriculum and then re-picking it by hand is the
  // difference between "plans for THIS curriculum" and "plans, somewhere".
  // Read once: switching tabs on the parent page rewrites the query string, and
  // a filter that kept re-reading it would snap back on the next tab click.
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<Record<string, string>>(() => ({
    board: searchParams.get('board') || '',
    grade: searchParams.get('grade') || '',
    from: '',
    visibility: '',
  }));

  const { data: curricula } = useQuery({
    queryKey: ['curricula'],
    queryFn: () => curriculumApi.listCurricula(),
  });

  /** Only published, non-teacher curricula are worth planning against — a plan
   *  bound to a draft syllabus would name chapters that may still move. */
  const plannable = useMemo(
    () => (curricula?.curricula ?? [])
      .filter(c => c.ownerType !== 'teacher' && c.isActive !== false),
    [curricula],
  );

  /**
   * Every published plan, whoever owns it — ONE row each.
   *
   * Asked without a subject on purpose. The endpoint can narrow to plans covering
   * one, and this page used to call it once per subject and pool the answers,
   * which listed a ten-subject plan ten times.
   */
  const { data: listed } = useQuery({
    queryKey: ['listed-study-plans'],
    queryFn: () => studyPlansApi.listListed(),
  });

  const boardOptions = useMemo(
    () => [...new Set(plannable.map(c => c.board).filter(Boolean))]
      .sort()
      .map(b => ({ value: String(b), label: String(b).toUpperCase() })),
    [plannable],
  );

  /**
   * Board and grade narrow the list; either may be left at "any".
   *
   * They used to be required, on the reasoning that a plan is written FOR a
   * level and "all levels" would offer New plan on a page that cannot say what
   * the plan is for. But that conflated BROWSING with AUTHORING: the level a new
   * plan is stamped with is `stampLevel`, which is already undefined unless both
   * halves are known, and a plan created without one takes its level in the
   * editor. Forcing a level to read the list only hid every other plan.
   *
   * No first-option fallback either: with the empty placeholder restored, an
   * unset filter is shown as unset, so the value and what is on screen agree.
   */
  const board = filters.board || '';

  const gradeOptions = useMemo(
    () => [...new Set(plannable
      .filter(c => !board || c.board === board)
      .map(c => String(c.grade ?? ''))
      .filter(Boolean))]
      .sort((a, b) => Number(a) - Number(b))
      .map(g => ({ value: g, label: `Grade ${g}` })),
    [plannable, board],
  );

  const grade = filters.grade || '';

  /**
   * The plan on screen, when one is open.
   *
   * Its own board and grade decide what its subject chips can offer and which
   * chapters they date against — the page's filters are a BROWSE, and with them
   * at "all levels" nothing resolved at all: every subject reported "there is no
   * curriculum for X, so it cannot be added to a plan" while the plan itself
   * plainly said CBSE Grade 12.
   */
  const [openPlan, setOpenPlan] = useState<StudyPlan | null>(null);
  const level = { board: openPlan?.board || board, grade: openPlan?.grade || grade };

  /** Stamped on a plan authored here — only when BOTH halves are known, since a
   *  curriculum is a (board, grade, subject) triple and half of one resolves
   *  nothing. Absent means the plan takes its level in the editor instead. */
  const stampLevel = level.board && level.grade ? { board: level.board, grade: level.grade } : undefined;

  /**
   * EVERY subject a plan may cover — core and extended alike — each carrying the
   * syllabus for the chosen level when one exists.
   *
   * Resolved at `level`, NOT at the filter bar: an open plan's own board and grade
   * decide its syllabuses. Reading the filters here meant a plan opened while they
   * said "Any board" resolved no curriculum for any subject, and its chapter
   * schedule reported "no chapters resolved for this syllabus" over a plan whose
   * chapters were all dated.
   *
   * It used to list only the subjects that HAVE a syllabus, so a level with three
   * curricula offered three chips and said nothing about the rest: a missing
   * curriculum read as a missing subject. The editor draws a subject with no
   * syllabus differently and explains why it cannot be taken, which is the honest
   * version of the same guard — and the one that says what to fix.
   */
  const availableSubjects = useMemo(() => {
    // Keyed by plain string: the map is built from curricula, whose `subject` is
    // the core four, but it is looked up with ALL_SUBJECT_OPTIONS, which carries
    // the extended ones too. A subject with no syllabus at this level is meant
    // to miss and come back undefined — that is the "plannable, no chapters"
    // case — so the lookup must be allowed to ask.
    const atLevel = new Map<string, string>(plannable
      .filter(c => c.board === level.board && String(c.grade ?? '') === level.grade)
      .map(c => [c.subject, c.id]));
    return ALL_SUBJECT_OPTIONS.map(subject => ({ subject, curriculumId: atLevel.get(subject) }));
  }, [plannable, level.board, level.grade]);

  /** The same chapter fetch the class tab uses. A subject with no syllabus at
   *  this level carries no curriculum id, and the hook asks for nothing. */
  const chaptersFor = useChaptersFor(availableSubjects);

  /** The levels a plan may be written for, from the syllabuses that exist. */
  const boardGradeOptions = useMemo(() => {
    const byBoard = new Map<string, Set<string>>();
    for (const c of plannable) {
      if (!c.board || !c.grade) continue;
      byBoard.set(String(c.board), (byBoard.get(String(c.board)) ?? new Set()).add(String(c.grade)));
    }
    return [...byBoard.entries()]
      .map(([b, grades]) => ({ board: b, grades: [...grades].sort((x, y) => Number(x) - Number(y)) }))
      .sort((a, b) => a.board.localeCompare(b.board));
  }, [plannable]);

  /** Published plans from every OTHER owner. Ours are excluded because the panel
   *  loads those itself — carrying them twice would be a duplicate row. */
  const others: StudyPlan[] = useMemo(
    () => (listed?.plans ?? []).filter(p => p.ownerKind !== 'platform'),
    [listed],
  );

  /**
   * One predicate for every row, ours and theirs alike.
   *
   * Handed to the panel rather than applied to the list here, because the panel
   * owns the plans it fetched — it creates into and deletes from that list, and a
   * caller filtering it would be a second opinion about what it contains.
   */
  const showPlan = useCallback(
    (p: StudyPlan) =>
      (!filters.from || p.ownerKind === filters.from)
      && (!filters.visibility || p.visibility === filters.visibility)
      && (!board || p.board === board)
      && (!grade || String(p.grade ?? '') === grade),
    // Stable while the filters are: the panel keys its row list on this, so a new
    // identity every render would rebuild the list on every render.
    [filters.from, filters.visibility, board, grade],
  );

  const filterDefs: FilterDef[] = [
    { key: 'from', placeholder: 'From anyone', options: FROM_OPTIONS },
    { key: 'visibility', placeholder: 'Any visibility', options: VISIBILITY_OPTIONS },
    { key: 'board', placeholder: 'Any board', options: boardOptions },
    { key: 'grade', placeholder: 'Any grade', options: gradeOptions },
  ];

  return (
    <section className="school-section">
      <PlansPanel
        api={classSharedApi}
        ownerKind="platform"
        subjectName={subjectLabel}
        availableSubjects={availableSubjects}
        chaptersFor={chaptersFor}
        // Published plans from every other owner, in the same table as ours.
        additionalPlans={others}
        // Ours are the only ones authored here. Someone else's published plan is
        // shown for oversight, and the act available on it — unlisting — is the
        // visibility control, which `canPublishPlan` allows.
        canEditPlan={(p) => p.ownerKind === 'platform'}
        canPublishPlan={() => true}
        // Which plan a level's students are started on is decided here and
        // nowhere else — it is the platform's answer for that board and grade,
        // and this is the platform's surface.
        canSetDefault
        showPlan={showPlan}
        onSelectionChange={setOpenPlan}
        // Handed to the panel so it appears with the LIST and not over a plan.
        listHeader={
          <SearchFilterBar
            filters={filterDefs}
            values={filters}
            onChange={setFilters}
            hideSearch
            hideFetch
          />
        }
        // The chosen board and grade are stamped on a plan authored here, so it
        // resolves a syllabus for any subject added later rather than depending
        // on this page's filters still being set the same way.
        boardGrade={stampLevel}
        // …and can be re-aimed afterwards, from the same set of levels the
        // filters above are built from.
        boardGradeOptions={boardGradeOptions}
      />
    </section>
  );
}
